import React, { useMemo } from "react";
import { observer } from "mobx-react";
import { Alert, Checkbox } from "antd";
import List from "@app/components/list/ordered";
import Loader from "@app/components/loader/content";

import FieldList from "./field-list";
import SourceDocsPicker from "./source-docs-picker";
import ArticlePicker from "./article-picker";
import alertIcon from "./alert-icon";

import "./style.scoped.scss";

const withMoved = (list, { from, to }) => {
    if (from < 0 || from >= list.length || to < 0 || to >= list.length) {
        return null;
    }
    const next = [...list];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
};

const rankedWithin = (prior, scope) => [
    ...prior.filter((id) => scope.includes(id)),
    ...scope.filter((id) => !prior.includes(id)),
];

const SectionPanel = observer(
    ({
        template,
        sectionKey,
        values,
        setValue,
        documents,
        articles,
        selectedSections,
        invalid,
    }) => {
        const nameById = useMemo(() => {
            const map = new Map();
            (documents || []).forEach((d) => map.set(d._id, d.name));
            return map;
        }, [documents]);

        const entry = template.sectionCatalog.find((s) => s.key === sectionKey);
        const sectionLabel = entry?.label || sectionKey;
        const articleType = template.sectionArticleType[sectionKey];
        const channel = articleType ? (articles || {})[articleType] : null;
        const articleById = useMemo(() => {
            const map = new Map();
            (channel || []).forEach((a) => map.set(a._id, a));
            return map;
        }, [channel]);

        const sectionDocuments = values.section_documents || {};
        const sectionPriority = values.section_priority || {};
        const priorityEnabled = values.section_priority_enabled || {};
        const sectionArticles = values.section_articles || {};

        const takesDocuments = !template.sectionsWithoutDocuments.has(sectionKey);
        const takesArticles = Boolean(articleType);
        const optional = Boolean(template.optionalSections?.has(sectionKey));

        const loaded = documents !== null && documents !== undefined;
        const articlesLoaded = channel !== null && channel !== undefined;

        const assigned = useMemo(() => {
            const ids = Array.isArray(sectionDocuments[sectionKey])
                ? sectionDocuments[sectionKey]
                : [];
            return loaded ? ids.filter((id) => nameById.has(id)) : ids;
        }, [sectionDocuments, sectionKey, nameById, loaded]);

        const assignedArticles = useMemo(() => {
            const ids = Array.isArray(sectionArticles[sectionKey])
                ? sectionArticles[sectionKey]
                : [];
            return articlesLoaded ? ids.filter((id) => articleById.has(id)) : ids;
        }, [sectionArticles, sectionKey, articleById, articlesLoaded]);

        const setAssigned = (nextDocs) => {
            setValue("section_documents", { ...sectionDocuments, [sectionKey]: nextDocs });

            const currentPri = Array.isArray(sectionPriority[sectionKey])
                ? sectionPriority[sectionKey]
                : [];
            if (!priorityEnabled[sectionKey]) {
                const pruned = currentPri.filter((id) => nextDocs.includes(id));
                if (pruned.length !== currentPri.length) {
                    setValue("section_priority", { ...sectionPriority, [sectionKey]: pruned });
                }
                return;
            }
            setValue("section_priority", {
                ...sectionPriority,
                [sectionKey]: rankedWithin(currentPri, nextDocs),
            });
        };

        const setAssignedArticles = (nextArticles) => {
            setValue("section_articles", { ...sectionArticles, [sectionKey]: nextArticles });
        };

        const togglePriority = () => {
            const enabled = !priorityEnabled[sectionKey];
            setValue("section_priority_enabled", { ...priorityEnabled, [sectionKey]: enabled });
            if (enabled) {
                const prior = Array.isArray(sectionPriority[sectionKey])
                    ? sectionPriority[sectionKey]
                    : [];
                setValue("section_priority", {
                    ...sectionPriority,
                    [sectionKey]: rankedWithin(prior, assigned),
                });
            }
        };

        const ranking = priorityEnabled[sectionKey]
            ? (sectionPriority[sectionKey] || []).filter((id) => assigned.includes(id))
            : [];
        const covered = ranking.length === assigned.length;

        const reorderArticles = (move) => {
            const next = withMoved(assignedArticles, move);
            if (next) {
                setValue("section_articles", { ...sectionArticles, [sectionKey]: next });
            }
        };

        const reorderRanking = (move) => {
            const next = withMoved(ranking, move);
            if (next) {
                setValue("section_priority", { ...sectionPriority, [sectionKey]: next });
            }
        };

        const orderedList = (ids, onMove, labelOf) => (
            <List id={`order-${sectionKey}`} onChange={onMove}>
                {ids.map((id, index) => (
                    <List.Item key={id} id={id} index={index}>
                        <div className="priority-item">
                            <span className="rank">{index + 1}</span>
                            <span className="name">{labelOf(id)}</span>
                        </div>
                    </List.Item>
                ))}
            </List>
        );

        const articleLabel = (id) => {
            const article = articleById.get(id);
            return article ? `${article.cid} ${article.title || ""}`.trim() : id;
        };

        const advisories = template.advisories?.(sectionKey, values) || [];

        return (
            <div className="cer-section-panel">
                {advisories
                    .filter((a) => !a.field)
                    .map((a, i) => (
                        <Alert
                            key={i}
                            className="advisory"
                            showIcon
                            icon={alertIcon(a.type)}
                            {...a}
                        />
                    ))}

                <FieldList
                    template={template}
                    section={sectionKey}
                    values={values}
                    advisories={advisories.filter((a) => a.field)}
                    setValue={setValue}
                    selectedSections={selectedSections}
                    invalid={invalid}
                />

                {!takesDocuments && !takesArticles && (
                    <div className="cer-field section-note">
                        <label>Documents for this section</label>
                        <div className="field-description">
                            None. This section is configured entirely from the fields above — it
                            reads no source documents, so there is nothing to assign or prioritise.
                        </div>
                    </div>
                )}

                {takesDocuments && (
                    <div className="cer-field">
                        <label>{entry?.documentsLabel || "Documents for this section"}</label>
                        <div className="field-description">
                            {entry?.description ||
                                "The files this section is allowed to read. All of them are searched co-equally unless a priority order is set below."}
                        </div>
                        {loaded ? (
                            <SourceDocsPicker
                                documents={documents}
                                value={assigned}
                                onChange={setAssigned}
                            />
                        ) : (
                            <Loader loading={true} />
                        )}
                    </div>
                )}

                {takesDocuments && loaded && assigned.length === 0 && optional && (
                    <Alert
                        className="advisory"
                        type="info"
                        showIcon
                        message="This section can be left empty"
                        description={`Nothing is assigned to "${sectionLabel}", which does not stop the run — generation goes ahead without it.`}
                    />
                )}

                {takesDocuments && loaded && assigned.length === 0 && !optional && (
                    <Alert
                        className="advisory"
                        type="warning"
                        showIcon
                        icon={alertIcon("warning")}
                        message="This section needs at least one file"
                    />
                )}

                {takesArticles && (
                    <div className="cer-field">
                        <label>{entry?.articlesLabel || "Articles for this section"}</label>
                        {entry?.articlesDescription && (
                            <div className="field-description">{entry.articlesDescription}</div>
                        )}
                        {articlesLoaded ? (
                            <ArticlePicker
                                articles={channel}
                                value={assignedArticles}
                                onChange={setAssignedArticles}
                            />
                        ) : (
                            <Loader loading={true} />
                        )}
                    </div>
                )}

                {takesArticles && articlesLoaded && assignedArticles.length === 0 && (
                    <Alert
                        className="advisory"
                        type="warning"
                        showIcon
                        icon={alertIcon("warning")}
                        message="This section needs at least one article"
                    />
                )}

                {takesArticles && articlesLoaded && sectionKey === template.orderedSectionKey && (
                    <div className="cer-field">
                        <label>Article Order</label>
                        <div className="field-description">
                            Arrange articles in the desired order for presentation and generation
                            within summary tables and outputs.
                        </div>
                        {assignedArticles.length > 0 ? (
                            orderedList(assignedArticles, reorderArticles, articleLabel)
                        ) : (
                            <div className="list-empty">
                                Assign articles to this section to order them.
                            </div>
                        )}
                    </div>
                )}

                {takesDocuments && (
                    <div className="cer-field priority-control">
                        <Checkbox checked={!!priorityEnabled[sectionKey]} onChange={togglePriority}>
                            Set a priority order between this section&apos;s files
                        </Checkbox>
                        <div className="field-description">
                            A tie-breaker: when the same fact appears in more than one file, the
                            earliest-ranked wins.
                        </div>
                        {priorityEnabled[sectionKey] &&
                            (assigned.length === 0 ? (
                                <div className="priority-empty">
                                    Assign files to this section to rank them.
                                </div>
                            ) : (
                                <>
                                    <div
                                        className={`priority-coverage${covered ? "" : " partial"}`}
                                    >
                                        {covered
                                            ? `Ranks all ${assigned.length} assigned file(s).`
                                            : `Ranks ${ranking.length} of ${assigned.length} assigned file(s) — a partial ranking would exclude the rest from this section, so it is dropped and the files are searched co-equally.`}
                                    </div>
                                    {orderedList(
                                        ranking,
                                        reorderRanking,
                                        (id) => nameById.get(id) || id,
                                    )}
                                </>
                            ))}
                    </div>
                )}
            </div>
        );
    },
);

export default SectionPanel;
